import type { Vec3 } from '@rcs/domain';

/**
 * 制御点を移動するときの制約（FR-EDT-005）。
 *
 * - `free`：制限なし（カメラに正対する平面の上）
 * - `horizontal`：水平面固定（高さは変えず、X・Z を動かす）
 * - `x` / `y` / `z`：その軸の方向だけに動かす（`y` が垂直方向固定）
 */
export type MoveConstraint = 'free' | 'horizontal' | 'x' | 'y' | 'z';

/** 制約の一覧（表示順）。 */
export const MOVE_CONSTRAINTS: readonly MoveConstraint[] = ['free', 'horizontal', 'x', 'y', 'z'];

/** グリッドスナップの設定（FR-SNP-001）。 */
export interface SnapSettings {
  readonly enabled: boolean;
  /** グリッドの間隔 [m] */
  readonly grid: number;
}

/** 選べるグリッドの間隔 [m]。 */
export const SNAP_GRIDS: readonly number[] = [0.5, 1, 2, 5, 10];

/** 既定のスナップ設定：オフ、間隔 1 m。 */
export const DEFAULT_SNAP: SnapSettings = { enabled: false, grid: 1 };

/** 値を、最も近いグリッド（`grid` の整数倍）に丸める。`grid` が正でなければそのまま。 */
export function snapValue(value: number, grid: number): number {
  if (!(grid > 0)) return value;
  const snapped = Math.round(value / grid) * grid;
  // −0 や、0.1 + 0.2 のような誤差を出さない
  return snapped === 0 ? 0 : Number(snapped.toFixed(10));
}

/** 位置の 3 成分のうち、動かしてよい成分（制約が許す軸）。 */
function freeAxes(constraint: MoveConstraint): readonly [boolean, boolean, boolean] {
  switch (constraint) {
    case 'free':
      return [true, true, true];
    case 'horizontal':
      return [true, false, true];
    case 'x':
      return [true, false, false];
    case 'y':
      return [false, true, false];
    case 'z':
      return [false, false, true];
  }
}

/**
 * 移動の制約を適用する。`start` から `target` へ動かすとき、制約が許さない成分は `start` のまま。
 */
export function constrainMove(start: Vec3, target: Vec3, constraint: MoveConstraint): Vec3 {
  const [fx, fy, fz] = freeAxes(constraint);
  return [fx ? target[0] : start[0], fy ? target[1] : start[1], fz ? target[2] : start[2]];
}

/**
 * 移動後の位置を求める：制約を適用し、スナップが有効なら、動かしてよい成分だけをグリッドに丸める
 * （制約で固定した成分は、`start` の値のまま変えない）。
 */
export function applyMove(
  start: Vec3,
  target: Vec3,
  constraint: MoveConstraint = 'free',
  snap: SnapSettings = DEFAULT_SNAP,
): Vec3 {
  const constrained = constrainMove(start, target, constraint);
  if (!snap.enabled) return constrained;
  const [fx, fy, fz] = freeAxes(constraint);
  return [
    fx ? snapValue(constrained[0], snap.grid) : constrained[0],
    fy ? snapValue(constrained[1], snap.grid) : constrained[1],
    fz ? snapValue(constrained[2], snap.grid) : constrained[2],
  ];
}
